/*
 * Copyright (c) 2024, King Living
 * FSD #5 + KINDW-1380: For /product/:id and /category/:id, SCAPI must confirm the resource on the
 * target site; otherwise home. For other paths, Contentful (region-aware) then SFCC category on the
 * target site must resolve like DynamicPage; if neither exists, redirect to home (FSD region switch).
 */

import {print} from 'graphql';

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {
  PASSWORDLESS_LOGIN_LANDING_PATH,
  RESET_PASSWORD_LANDING_PATH,
} from '@salesforce/retail-react-app/app/constants';
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url';
import {getSites} from '@salesforce/retail-react-app/app/utils/site-utils';

import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {getRegionFromLocale, selectPageByRegion} from '@amblique/king-living/app/contentful/helpers/region';
import {getPageBySlugForRegion} from '@amblique/king-living/app/contentful/queries/page.queries';
import {buildRegionHomeUrl, buildRegionSiteUrl} from '@amblique/king-living/app/utils/geo-region';

const MOCK_GEO_QUERY_PARAM = 'klMockGeoCountry';

/** Removed from target URL after region switch (dev-only debug flag, no longer used). */
const LEGACY_REGION_SWITCH_DEBUG_PARAM = 'klRegionSwitchDebug';

/**
 * GET Shopper Products getProduct through the PWA commerce proxy. Query `siteId` / `locale` must
 * match the target site — the commerce-sdk `shopperProducts` client merges CommerceApiProvider
 * defaults (current URL site), which breaks cross-site region-switch checks even with a
 * channel-correct Bearer token.
 *
 * @param {object} args
 * @param {string} args.organizationId
 * @param {string} args.siteId - Target site id
 * @param {string} args.locale - Target locale id
 * @param {string} args.productId
 * @param {string} args.accessToken
 * @returns {Promise<boolean>}
 */
async function scapiTargetSiteProductExists({
  organizationId,
  siteId: targetSiteId,
  locale,
  productId,
  accessToken,
}) {
  const {app} = getConfig();
  const proxyPath = app?.commerceAPI?.proxyPath || '/mobify/proxy/api';
  const path = `${proxyPath}/product/shopper-products/v1/organizations/${organizationId}/products/${encodeURIComponent(productId)}`;
  const qs = new URLSearchParams({
    siteId: targetSiteId,
    locale,
    allImages: 'false',
    perPricebook: 'false',
  });

  qs.append('expand', 'none');

  const url = `${path}?${qs.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {Authorization: `Bearer ${accessToken}`},
    credentials: 'include',
  });

  if (!res.ok) {
    try {
      await res.text();
    } catch {
      // ignore
    }

    return false;
  }

  return true;
}

/**
 * @param {object} args
 * @param {string} args.organizationId
 * @param {string} args.siteId
 * @param {string} args.locale
 * @param {string} args.categoryId
 * @param {string} args.accessToken
 * @returns {Promise<boolean>}
 */
async function scapiTargetSiteCategoryExists({
  organizationId,
  siteId: targetSiteId,
  locale,
  categoryId,
  accessToken,
}) {
  const {app} = getConfig();
  const proxyPath = app?.commerceAPI?.proxyPath || '/mobify/proxy/api';
  const path = `${proxyPath}/product/shopper-products/v1/organizations/${organizationId}/categories/${encodeURIComponent(categoryId)}`;
  const qs = new URLSearchParams({
    siteId: targetSiteId,
    locale,
    levels: '0',
  });
  const url = `${path}?${qs.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {Authorization: `Bearer ${accessToken}`},
    credentials: 'include',
  });

  if (!res.ok) {
    try {
      await res.text();
    } catch {
      // ignore
    }

    return false;
  }

  return true;
}

/**
 * Guest token for the target site's channel (SLAS), minted server-side. Required because the
 * shopper's browser token is scoped to the current site and SCAPI rejects cross-channel use.
 *
 * @param {string} targetSiteId
 * @returns {Promise<string|null>}
 */
async function fetchTargetSiteGuestToken(targetSiteId) {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const res = await fetch('/server/region-switch-guest-token', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({siteId: targetSiteId}),
      credentials: 'same-origin',
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();

    return data.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Remove dev-only geo mock query param so the target site does not re-trigger the modal.
 *
 * @param {string} [search] - location.search
 * @returns {string} Query string including `?`, or empty string
 */
export function sanitizeRegionSwitchSearch(search) {
  if (!search || search === '?') {
    return '';
  }

  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);

  params.delete(MOCK_GEO_QUERY_PARAM);
  params.delete(LEGACY_REGION_SWITCH_DEBUG_PARAM);
  const s = params.toString();

  return s ? `?${s}` : '';
}

/**
 * @param {object} args
 * @param {object} args.shopperProducts - unused; kept for call-site compatibility (checks use fetch)
 * @param {() => Promise<string>} args.getToken
 * @param {string} [args.currentSiteId] - Shopper's current site; when different from target, use server token for SCAPI
 * @param {string} args.organizationId
 * @param {string} args.targetSiteId
 * @param {string} args.locale - Target site default locale id
 * @param {string} args.pathnameOnly - Path without site/locale prefix, e.g. /product/SKU
 * @param {string} args.search - Sanitized search (may include `?`)
 * @returns {Promise<boolean|null>} true = exists, false = not on catalog, null = not product/category (caller uses home)
 */
async function commerceResourceExistsOnTargetSite({
  shopperProducts: _shopperProducts,
  getToken,
  currentSiteId,
  organizationId,
  targetSiteId,
  locale,
  pathnameOnly,
  search,
}) {
  void _shopperProducts;

  let token;

  if (currentSiteId && currentSiteId !== targetSiteId) {
    token = await fetchTargetSiteGuestToken(targetSiteId);
  }

  if (!token) {
    try {
      token = await getToken();
    } catch {
      return false;
    }
  }

  const productMatch = pathnameOnly.match(/^\/product\/([^/]+)\/?$/);

  if (productMatch) {
    const fromPath = decodeURIComponent(productMatch[1]);
    const q = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(q);
    const id = params.get('pid') || fromPath;

    return scapiTargetSiteProductExists({
      organizationId,
      siteId: targetSiteId,
      locale,
      productId: id,
      accessToken: token,
    });
  }

  const categoryMatch = pathnameOnly.match(/^\/category\/([^/]+)\/?$/);

  if (categoryMatch) {
    const id = decodeURIComponent(categoryMatch[1]);

    return scapiTargetSiteCategoryExists({
      organizationId,
      siteId: targetSiteId,
      locale,
      categoryId: id,
      accessToken: token,
    });
  }

  return null;
}

/**
 * Routes that exist on every storefront and do not use the DynamicPage catch-all resolution.
 * Region switch keeps the same path without a Contentful/category probe (KINDW-1380).
 *
 * @param {string} normalizedPath - Path without site/locale prefix
 * @returns {boolean}
 */
function shouldAssumePathExistsOnAllSites(normalizedPath) {
  const raw = typeof normalizedPath === 'string' ? normalizedPath.split('?')[0] : '';
  const p = (raw.replace(/\/+$/, '') || '/').toLowerCase();

  if (p === '/') {
    return true;
  }

  const fixedPaths = new Set([
    '/login',
    '/registration',
    '/reset-password',
    '/callback',
    '/cart',
    '/cart/upsell',
    '/store-locator',
    '/showrooms',
    '/test-geocoding',
    '/styling-check'
  ]);

  if (fixedPaths.has(p)) {
    return true;
  }

  if (p === RESET_PASSWORD_LANDING_PATH.toLowerCase() || p === PASSWORDLESS_LOGIN_LANDING_PATH.toLowerCase()) {
    return true;
  }

  if (p.startsWith('/account') || p.startsWith('/checkout') || p.startsWith('/search')) {
    return true;
  }

  if (p.startsWith('/careers/')) {
    return true;
  }

  let socialPath = '/social-callback';

  try {
    socialPath = getConfig()?.app?.login?.social?.redirectURI || '/social-callback';
  } catch {
    // keep default
  }

  const socialNorm = String(socialPath).replace(/\/+$/, '').toLowerCase();

  if (socialNorm && (p === socialNorm || p.startsWith('/social-callback'))) {
    return true;
  }

  if (p.startsWith('/mobify/') || p.startsWith('/.well-known')) {
    return true;
  }

  return false;
}

/**
 * Same resolution order as DynamicPage: Contentful page (slug primary + /slug fallback, region-aware)
 * then SFCC category by path-derived id (skipped for page/* slugs).
 *
 * @param {object} args
 * @param {object} args.targetSite - getSites() entry for destination
 * @param {string} args.normalizedPath - e.g. /offers-and-competitions
 * @param {string} args.organizationId
 * @param {() => Promise<string>} args.getToken
 * @param {string} [args.currentSiteId]
 * @returns {Promise<boolean>} true if the path should resolve on the target site
 */
async function targetSiteResolvesDynamicPath({
  targetSite,
  normalizedPath,
  organizationId,
  getToken,
  currentSiteId,
}) {
  const pathOnly = (normalizedPath || '/').split('?')[0];
  const slugSegment = pathOnly.replace(/^\/+|\/+$/g, '');

  if (!slugSegment) {
    return true;
  }

  if (!targetSite?.preferences?.contentful?.spaceId || !targetSite?.preferences?.contentful?.deliveryAccessToken) {
    return true;
  }

  const defaultLocaleId = String(targetSite.l10n?.defaultLocale || '').replace(/_/g, '-');
  const localeForService = {id: defaultLocaleId};

  let contentful;

  try {
    contentful = ContentfulService.instantiate({
      site: targetSite,
      locale: localeForService,
    });
  } catch {
    return true;
  }

  const graphqlLocale =
    typeof contentful.locale === 'string' ? contentful.locale : contentful.variables?.locale?.id || defaultLocaleId;
  const targetRegion = getRegionFromLocale(graphqlLocale, targetSite.id);
  const scapiLocale = defaultLocaleId;

  const fetchPageForSlug = async (slugSegment) => {
    try {
      const slugs = slugSegment.startsWith('/') ?
        [slugSegment, slugSegment.slice(1)].filter(Boolean) :
        [slugSegment, `/${slugSegment}`];
      const data = await contentful.query(print(getPageBySlugForRegion), {
        locale: graphqlLocale,
        preview: false,
        slugs: [...new Set(slugs)],
      });
      const items = data?.pageCollection?.items ?? [];

      return selectPageByRegion(items, targetRegion);
    } catch {
      return null;
    }
  };

  const page = await fetchPageForSlug(slugSegment);

  if (page) {
    return true;
  }

  const isContentPath = slugSegment.startsWith('page/');

  if (isContentPath) {
    return false;
  }

  const categoryId = slugSegment.replace(/\//g, '-');

  if (!categoryId) {
    return false;
  }

  const pathnameForScapi = `/category/${encodeURIComponent(categoryId)}`;
  const exists = await commerceResourceExistsOnTargetSite({
    shopperProducts: null,
    getToken,
    currentSiteId,
    organizationId,
    targetSiteId: targetSite.id,
    locale: scapiLocale,
    pathnameOnly: pathnameForScapi,
    search: '',
  });

  return exists === true;
}

/**
 * Resolve final URL for regional switch (FSD #5 + catalog deep-link check).
 *
 * @param {object} args
 * @param {{ siteId: string }} args.siteConfigEntry
 * @param {string} args.pathname - location.pathname
 * @param {string} [args.search] - location.search
 * @param {object} args.shopperProducts
 * @param {() => Promise<string>} args.getToken
 * @param {string} [args.currentSiteId] - Current storefront site id (e.g. from useMultiSite)
 * @returns {Promise<string|null>}
 */
export async function resolveRegionSwitchTargetUrl({
  siteConfigEntry,
  pathname,
  search = '',
  shopperProducts,
  getToken,
  currentSiteId,
}) {
  const homeUrl = buildRegionHomeUrl(siteConfigEntry);

  if (!homeUrl) {
    return null;
  }

  const {app} = getConfig();
  const organizationId = app?.commerceAPI?.parameters?.organizationId;

  if (!organizationId) {
    return homeUrl;
  }

  let sites;

  try {
    sites = getSites();
  } catch {
    return homeUrl;
  }

  const targetSite = sites.find((s) => s.id === siteConfigEntry.siteId);
  const locale = targetSite?.l10n?.defaultLocale;

  if (!locale) {
    return homeUrl;
  }

  const cleanSearch = sanitizeRegionSwitchSearch(search);
  const barePath = removeSiteLocaleFromPath(pathname) || '/';
  const normalizedPath = barePath.startsWith('/') ? barePath : `/${barePath}`;
  const pathAndQuery = `${normalizedPath}${cleanSearch}`;

  if (normalizedPath === '/' || normalizedPath === '') {
    return homeUrl;
  }

  const needsTargetCatalogCheck =
    /^\/product\/[^/]+\/?$/.test(normalizedPath) || /^\/category\/[^/]+\/?$/.test(normalizedPath);

  if (needsTargetCatalogCheck) {
    const existsResult = await commerceResourceExistsOnTargetSite({
      shopperProducts,
      getToken,
      currentSiteId,
      organizationId,
      targetSiteId: siteConfigEntry.siteId,
      locale,
      pathnameOnly: normalizedPath,
      search: cleanSearch,
    });

    if (existsResult !== true) {
      return homeUrl;
    }
  }

  if (!needsTargetCatalogCheck && !shouldAssumePathExistsOnAllSites(normalizedPath)) {
    const dynamicOk = await targetSiteResolvesDynamicPath({
      targetSite,
      normalizedPath,
      organizationId,
      getToken,
      currentSiteId,
    });

    if (!dynamicOk) {
      return homeUrl;
    }
  }

  const preservedUrl = buildRegionSiteUrl(siteConfigEntry, pathAndQuery);

  return preservedUrl || homeUrl;
}
